import type { ClintplayEvent, EventBatch } from './types';

interface TransportConfig {
  apiUrl: string;
  projectId: string;
  sessionId: string;
  flushIntervalMs: number;
  flushMaxBytes: number;
  compressEvents: boolean;
  anonId?: string;
  /** Build version of this SDK (index.ts `VERSION`, inlined from
   *  package.json). Optional here so a Transport constructed directly — as
   *  the unit tests do — stays valid; `init()` always passes it. */
  sdkVersion?: string;
}

// Gzip the JSON body via the browser's built-in CompressionStream API.
// rrweb-heavy payloads compress 5-10x. Returns null if the browser doesn't
// support CompressionStream (Safari < 16.4, very old Chrome/Firefox) or if
// compression unexpectedly fails — caller falls back to plain JSON in that
// case so the request still goes through.
async function gzipBody(body: string): Promise<Blob | null> {
  if (typeof CompressionStream === 'undefined') return null;
  try {
    const stream = new Blob([body]).stream().pipeThrough(new CompressionStream('gzip'));
    return await new Response(stream).blob();
  } catch {
    return null;
  }
}

// The fetch spec caps the bodies of the keepalive requests a document has in
// flight at 64 KiB IN TOTAL: a request that would cross it is a network error
// at call time (Chromium: `TypeError: Failed to fetch`). This is what OUR
// in-flight keepalive bodies may add up to. It stays under 64 KiB because
// other scripts on the page (the host app's Sentry, say) draw from the same
// quota, and we cannot see what they hold.
const KEEPALIVE_BUDGET = 60_000;

// Don't fire threshold-driven flushes more than once per second. rrweb can
// emit hundreds of events in a burst (mutations during a single keystroke,
// scroll); without this throttle a busy SPA produces a flush every couple
// hundred ms, which floods the customer's Network panel and stacks up
// against per-origin connection limits on HTTP/1.1 backends.
const MIN_FLUSH_INTERVAL_MS = 1000;

// A batch answered 408/429/5xx, lost to a network error, or left unanswered
// past its timeout is kept and re-sent, oldest first. Backoff doubles from
// 1 s to a 30 s ceiling (1, 2, 4, 8, 16, 30, 30 s, each jittered down to
// half), so a batch gets 8 attempts with 45-91 s of backoff between them
// (time spent waiting on answers comes on top): meant to outlast a short api
// outage or a pod being replaced.
//
// A 500 gets the same 8. The api returns 500 when the database does not answer
// and the process has no cached key (authMiddleware), which is every fresh
// pod's situation during a database stall. The cost: a payload that meets an
// api bug holds the batches behind it for those attempts before it is dropped.
const MAX_ATTEMPTS = 8;
const BASE_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 30_000;

// A request with no answer is abandoned after 30 s plus the time its body
// takes to upload at 20 KB/s (a 1 MB FullSnapshot gets 80 s), then treated as
// a failed attempt. Without it, one re-send that never answers would hold the
// queue, and every batch behind it, until some proxy gave up. Each attempt
// that timed out doubles the next one's allowance, up to 4x, so an upload
// slower than 20 KB/s is not abandoned over and over. The ceiling keeps a
// long outage where nothing answers from growing the wait past it: uncapped,
// the 8th attempt of a small batch would wait an hour.
const REQUEST_TIMEOUT_MS = 30_000;
const SLOWEST_UPLOAD_BYTES_PER_MS = 20;
const MAX_TIMEOUT_DOUBLINGS = 2;

// What the re-send queue of one Transport may hold, in payload bytes as sent
// (gzip when the browser has CompressionStream), not counting requests in
// flight. A busy rrweb session produces on the order of 40 KB of gzip per
// second, so this is a minute or more of outage.
const MAX_QUEUED_BYTES = 4 * 1024 * 1024;

interface Undelivered {
  batches: number;
  events: number;
}

interface PendingBatch {
  payload: BodyInit;
  payloadSize: number;
  gzip: boolean;
  eventCount: number;
  /** The losses this batch reports in its envelope. If it is lost too, they
   *  go back to the tally, so a loss rides at most one delivered batch. (None,
   *  if it happens after the last flush.) */
  carried: Undelivered;
  /** Scheduled attempts so far. Sends forced by the page going away do not
   *  count: switching tabs during an outage would otherwise use them up. */
  attempts: number;
  timeouts: number;
  lastFailure: string;
  inFlight: boolean;
}

type Outcome = 'delivered' | 'retry' | 'rejected';

function isRetryableStatus(status: number): boolean {
  return status === 408 || status === 429 || status >= 500;
}

export class Transport {
  private buffer: ClintplayEvent[] = [];
  private bufferBytes = 0;
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private pendingThresholdFlush: ReturnType<typeof setTimeout> | null = null;
  private lastFlushAt = 0;
  private keepaliveBytesInFlight = 0;
  private retryQueue: PendingBatch[] = [];
  private retryQueueBytes = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private draining = false;
  // From the moment the page is hidden (or stop() runs) until it is visible
  // again. A batch that finishes compressing meanwhile, whichever flush built
  // it, does not wait behind the queue.
  private leaving = false;
  private undelivered: Undelivered = { batches: 0, events: 0 };

  constructor(private config: TransportConfig) {}

  start(): void {
    this.flushTimer = setInterval(() => {
      this.flush();
    }, this.config.flushIntervalMs);

    // Flush when the tab is backgrounded — covers tab switch and most
    // navigations even when the buffer hasn't hit either threshold, so we
    // don't lose the tail of a session because the user left before the
    // next interval tick. `keepalive` on the fetch, when the 64 KiB budget
    // allows it, handles the close-the-window case.
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    }
  }

  // Sends everything waiting to be re-sent and the buffer, now. Batches that
  // fail after this are still re-sent on their backoff timers, for as long as
  // their 8 attempts last (timeouts add to that), but a loss counted after
  // stop() has no later batch to report it.
  stop(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
    if (this.pendingThresholdFlush) {
      clearTimeout(this.pendingThresholdFlush);
      this.pendingThresholdFlush = null;
    }
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }
    this.leaving = true;
    this.sendEverythingNow();
  }

  send(event: ClintplayEvent): boolean {
    let eventSize: number;
    try {
      eventSize = JSON.stringify(event).length;
    } catch (err) {
      // JSON.stringify can throw on circular refs, BigInts, or pathologically
      // deep structures. Dropping a single bad event is far better than
      // letting the throw propagate into rrweb's record() — that would skip
      // initObservers and leave us with a session that has only the Meta
      // event and no replay.
      // eslint-disable-next-line no-console
      console.warn('[Clintplay] dropped unserializable event', event.type, err);
      return false;
    }
    this.buffer.push(event);
    this.bufferBytes += eventSize;
    if (this.bufferBytes >= this.config.flushMaxBytes) {
      this.scheduleThresholdFlush();
    }
    return true;
  }

  // Coalesce rapid threshold hits so we never fire more than one flush per
  // MIN_FLUSH_INTERVAL_MS. The interval timer is unaffected — it always
  // runs on its own schedule.
  private scheduleThresholdFlush(): void {
    if (this.pendingThresholdFlush) return;
    const elapsed = Date.now() - this.lastFlushAt;
    if (elapsed >= MIN_FLUSH_INTERVAL_MS) {
      this.flush();
    } else {
      this.pendingThresholdFlush = setTimeout(
        () => this.flush(),
        MIN_FLUSH_INTERVAL_MS - elapsed,
      );
    }
  }

  private onVisibilityChange = (): void => {
    if (typeof document === 'undefined') return;
    this.leaving = document.visibilityState === 'hidden';
    if (this.leaving) this.sendEverythingNow();
  };

  // Hidden is often the last moment this page runs: a backoff timer may never
  // fire, and a batch queued behind an in-flight re-send would wait for an
  // answer the page never gets. So every waiting batch and the buffer go out
  // now, concurrently (the api re-sorts by timestamp). Each takes keepalive
  // while the budget lasts, oldest first. Later rrweb batches depend on nodes
  // created in earlier ones: without a middle batch, the replay breaks until
  // the next FullSnapshot (#764). The cost of this order is the last batch,
  // which is the one index.ts fills with the final LCP/CLS/INP and often
  // holds the error or rage click right before the exit. It only loses out
  // when batches are waiting AND they and the last batch exceed 60 KB.
  private sendEverythingNow(): void {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    for (const item of this.retryQueue.filter((i) => !i.inFlight)) {
      this.dequeue(item);
      void this.attempt(item, false);
    }
    this.flush();
  }

  private flush(): void {
    if (this.pendingThresholdFlush) {
      clearTimeout(this.pendingThresholdFlush);
      this.pendingThresholdFlush = null;
    }
    if (this.buffer.length === 0) return;
    this.lastFlushAt = Date.now();
    const events = this.buffer;
    this.buffer = [];
    this.bufferBytes = 0;
    const carried = this.undelivered;
    this.undelivered = { batches: 0, events: 0 };
    const batch: EventBatch = {
      projectId: this.config.projectId,
      sessionId: this.config.sessionId,
      ...(this.config.anonId ? { anonId: this.config.anonId } : {}),
      // Omitted rather than sent empty when unknown: the API treats an
      // absent sdkVersion as "unknown" and stores NULL, and an empty
      // string would be a value that has to be special-cased downstream.
      ...(this.config.sdkVersion ? { sdkVersion: this.config.sdkVersion } : {}),
      ...(carried.batches > 0 ? { undelivered: carried } : {}),
      events,
      // Time the batch was built. A re-send reuses the same body, so it keeps
      // this value; the api only validates it (routes/events.ts).
      sentAt: Date.now(),
    };
    const body = JSON.stringify(batch);

    // Compress + send is async, but flush() callers don't await it, so the
    // buffer-clearing logic above stays sync.
    void this.dispatch(body, events.length, carried);
  }

  private async dispatch(body: string, eventCount: number, carried: Undelivered): Promise<void> {
    const gz = this.config.compressEvents ? await gzipBody(body) : null;
    const item: PendingBatch = {
      payload: gz ?? body,
      // Bytes on the wire, which is what the keepalive quota counts: UTF-8
      // for a string body, where .length (UTF-16 units) undercounts any
      // non-ASCII text. Only encoded when there is no gzip, since a
      // FullSnapshot body can be megabytes.
      payloadSize: gz ? gz.size : new TextEncoder().encode(body).length,
      gzip: gz !== null,
      eventCount,
      carried,
      attempts: 0,
      timeouts: 0,
      lastFailure: '',
      inFlight: false,
    };

    // Older batches are waiting to be re-sent: the api just shed load or was
    // unreachable. Wait behind them rather than going around the queue,
    // unless the page is going away (see sendEverythingNow).
    if (this.retryQueue.length > 0 && !this.leaving) {
      this.enqueue(item);
      return;
    }
    await this.attempt(item, true);
  }

  // One send outside the serial drain: a batch's first, or one forced by the
  // page going away (`counts` false). If it fails, the batch joins the queue.
  private async attempt(item: PendingBatch, counts: boolean): Promise<void> {
    const outcome = await this.deliver(item);
    if (counts) item.attempts++;
    if (outcome === 'delivered') return;
    if (outcome === 'retry' && !this.gaveUp(item)) this.enqueue(item);
    else this.drop(item);
  }

  private gaveUp(item: PendingBatch): boolean {
    if (item.attempts < MAX_ATTEMPTS) return false;
    item.lastFailure = `gave up after ${item.attempts} attempts, last: ${item.lastFailure}`;
    return true;
  }

  private enqueue(item: PendingBatch): void {
    if (this.retryQueueBytes + item.payloadSize > MAX_QUEUED_BYTES) {
      item.lastFailure = 're-send queue full';
      this.drop(item);
      return;
    }
    this.retryQueue.push(item);
    this.retryQueueBytes += item.payloadSize;
    this.scheduleDrain();
  }

  private dequeue(item: PendingBatch): void {
    const i = this.retryQueue.indexOf(item);
    if (i === -1) return;
    this.retryQueue.splice(i, 1);
    this.retryQueueBytes -= item.payloadSize;
  }

  private scheduleDrain(): void {
    if (this.retryTimer || this.draining || this.retryQueue.length === 0) return;
    // A batch that waited behind others and failed only in a send the page
    // forced has 0 attempts; it still waits the first step.
    const attempts = Math.max(this.retryQueue[0].attempts, 1);
    const base = Math.min(BASE_BACKOFF_MS * 2 ** (attempts - 1), MAX_BACKOFF_MS);
    // Jittered down to half, so tabs that failed together (a pod restart) do
    // not all come back in the same instant.
    const delay = base / 2 + Math.random() * (base / 2);
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      void this.drain();
    }, delay);
  }

  // Re-sends the queue oldest first. The head stays in the queue while it is
  // in flight, so a batch flushed meanwhile lines up behind it, and
  // sendEverythingNow leaves it alone.
  private async drain(): Promise<void> {
    // Defensive: only the backoff timer calls drain(), and scheduleDrain does
    // not arm it while draining, so no test can reach this line.
    if (this.draining) return;
    this.draining = true;
    try {
      while (this.retryQueue.length > 0) {
        const head = this.retryQueue[0];
        head.inFlight = true;
        let outcome: Outcome;
        try {
          outcome = await this.deliver(head);
        } finally {
          head.inFlight = false;
        }
        head.attempts++;
        if (outcome === 'retry' && !this.gaveUp(head)) break;
        this.dequeue(head);
        if (outcome !== 'delivered') this.drop(head);
      }
    } finally {
      this.draining = false;
    }
    this.scheduleDrain();
  }

  // The batch is gone for good. Say so in the console, and in the envelope of
  // the next batch built, so the loss reaches the api (routes/events.ts logs
  // it). If no batch is built after this, the loss stays in the console.
  private drop(item: PendingBatch): void {
    this.undelivered.batches += item.carried.batches + 1;
    this.undelivered.events += item.carried.events + item.eventCount;
    // eslint-disable-next-line no-console
    console.warn(
      `[Clintplay] dropped a batch of ${item.eventCount} events (${item.lastFailure})`,
    );
  }

  private async deliver(item: PendingBatch): Promise<Outcome> {
    const keepalive = this.keepaliveBytesInFlight + item.payloadSize <= KEEPALIVE_BUDGET;
    let outcome = await this.post(item, keepalive);
    if (outcome === 'network' && keepalive) {
      // A keepalive request the browser refuses is most likely over the
      // quota, held by another script we cannot see. A plain fetch is not
      // subject to it, so try once more right away. (A plain fetch is
      // cancelled if the page unloads mid-flight, which is what keepalive
      // was for, but the browser just refused that.)
      outcome = await this.post(item, false);
    }
    return outcome === 'network' ? 'retry' : outcome;
  }

  private async post(item: PendingBatch, keepalive: boolean): Promise<Outcome | 'network'> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'x-project-id': this.config.projectId,
      // Lets the API rate-limit per session instead of per project.
      // With per-project bucketing one chatty tab in production could
      // 429 every OTHER tab on the same project (events lost, identify
      // events lost → sessions show as anonymous). Per-session keeps
      // the bucket isolated.
      'x-session-id': this.config.sessionId,
    };
    if (item.gzip) headers['Content-Encoding'] = 'gzip';

    // There is no sendBeacon fallback (there was one up to 0.9.0). A beacon
    // cannot set headers, and the api's authMiddleware has read the key only
    // from x-api-key / x-project-id since the initial commit, so a beacon
    // that arrived got a 401. And a beacon is sent with keepalive, so it
    // draws from the same 64 KiB quota that just refused the fetch (#764).
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timeout = controller
      ? setTimeout(
          () => controller.abort(),
          (REQUEST_TIMEOUT_MS + item.payloadSize / SLOWEST_UPLOAD_BYTES_PER_MS) *
            2 ** Math.min(item.timeouts, MAX_TIMEOUT_DOUBLINGS),
        )
      : null;
    if (keepalive) this.keepaliveBytesInFlight += item.payloadSize;
    try {
      const res = await fetch(`${this.config.apiUrl}/events`, {
        method: 'POST',
        headers,
        body: item.payload,
        keepalive,
        signal: controller?.signal,
      });
      if (res.ok) return 'delivered';
      item.lastFailure = `HTTP ${res.status}`;
      return isRetryableStatus(res.status) ? 'retry' : 'rejected';
    } catch {
      if (controller?.signal.aborted) {
        // Not the quota refusing it (that is immediate): wait the backoff.
        item.timeouts++;
        item.lastFailure = 'no answer in time';
        return 'retry';
      }
      item.lastFailure = 'network error';
      return 'network';
    } finally {
      if (timeout) clearTimeout(timeout);
      if (keepalive) this.keepaliveBytesInFlight -= item.payloadSize;
    }
  }
}
